// Shared "I just got a valid table-session cookie, now set up localStorage/business context and go
// to the menu" logic. Originally lived only inside TableEntryPage.jsx (the physical-QR flow); the
// invite join flow (components/Table/TableSessionJoinPage.jsx) needs the exact same steps for a
// GUEST device, so this was extracted rather than duplicated to avoid the two flows drifting apart.
import { AuthFetchAPI } from "../api/Auth";
import { BranchOfficeFetchAPI } from "../api/BranchOffices";
import { BusinessFetchAPI } from "../api/Business";
import { applyTableSessionEcommerceContext, normalizeTableSessionPayload } from "../context/TableSessionContext";

const unwrapApiData = (response, keys = []) => {
  const root = response?.data?.data || response?.data || response;
  for (const key of keys) {
    if (root?.[key]) return root[key];
  }
  return root;
};

// GET /table-sessions/public/current (sanitizePublicSession on smartpay-dashboard) always includes
// business.id/branchoffice.id directly, but never includes a machine at all - the ecommerce machine
// only comes back from BranchOfficeFetchAPI.getBranchOfficeByCode's machine_ecommerce[0]. This fills
// in whatever is missing before the session can be trusted as the app's current business context.
export const resolveTableSessionEntryContext = async (session) => {
  const normalized = normalizeTableSessionPayload(session || {});
  let business = normalized.business;
  let branchoffice = normalized.branchoffice;
  let machine = normalized.machine;

  if (!business?.id && business?.code) {
    const response = await BusinessFetchAPI.getBusinessByCode(business.code);
    business = {
      ...business,
      ...unwrapApiData(response, ["business"]),
    };
  }

  if ((!branchoffice?.id || !machine?.id) && business?.code && branchoffice?.code) {
    const response = await BranchOfficeFetchAPI.getBranchOfficeByCode(business.code, branchoffice.code);
    const branchData = unwrapApiData(response, ["branchoffice", "branch"]);
    branchoffice = {
      ...branchoffice,
      ...branchData,
    };

    const ecommerceMachine = Array.isArray(branchData?.machine_ecommerce)
      ? branchData.machine_ecommerce[0]
      : branchData?.machine;
    if (ecommerceMachine) machine = ecommerceMachine;
  }

  return {
    ...normalized,
    business,
    branchoffice,
    machine,
  };
};

// Applies the resolved context to localStorage and best-effort mints the public token (never throws
// on that step - the table-session cookie remains the source of truth either way), returning the
// resolved session so the caller can decide where to navigate.
export const enterTableSessionMenu = async (session) => {
  const resolvedSession = await resolveTableSessionEntryContext(session);
  applyTableSessionEcommerceContext(resolvedSession);

  if (resolvedSession?.business?.id) {
    try {
      await AuthFetchAPI.generatePublicToken(resolvedSession.business.id);
    } catch (err) {
      // The table-session cookie remains the source of truth.
    }
  }

  return resolvedSession;
};
